// src/CqcContributors.js
// "Contributors" configuration panel — per site, the list of staff (name,
// role, email, mobile) that Worklist rows can be assigned to. Rendered as a
// modal overlay, opened from CqcWorklist.js's toolbar ("Manage contributors")
// — this is also reachable, per-row, via the "+ Add contributor…" option at
// the bottom of a Worklist row's assignee dropdown, which opens
// CqcQuickAddContributor.js instead (a lighter one-contributor popup that
// assigns the new contributor to that row immediately; this file is the
// full list — add/deactivate/reactivate/edit for a whole site at once).
// Contributors aren't app users and don't log in here — they're contact
// records an assignment points at, and an email address the Worklist's
// message thread (and the contributor-task email) can hand off to.

import React, { useEffect, useState } from "react";
import { cqcApi } from "./cqcApi";

const S = {
  overlay: { position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.45)", display: "flex",
             alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16 },
  modal: { background: "#fff", borderRadius: 10, width: "100%", maxWidth: 560, maxHeight: "82vh",
           display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: "system-ui, sans-serif" },
  head: { padding: "14px 18px", borderBottom: "1px solid #F0F0F0", display: "flex",
          alignItems: "center", justifyContent: "space-between", flexShrink: 0 },
  title: { fontSize: 15, fontWeight: 700, margin: 0 },
  closeBtn: { border: "none", background: "none", fontSize: 18, cursor: "pointer", color: "#5D5D5D", lineHeight: 1 },
  body: { padding: 18, overflow: "auto", flex: 1 },
  siteSelect: { width: "100%", padding: "8px 10px", fontSize: 13, border: "1px solid #D9D9D9", borderRadius: 6,
                marginBottom: 14, fontFamily: "inherit" },
  row: { display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid #F5F5F5" },
  name: { fontWeight: 600, fontSize: 13 },
  roleTag: { fontSize: 11, fontWeight: 600, color: "#5D5D5D", background: "#F5F5F5", border: "1px solid #F0F0F0",
             borderRadius: 999, padding: "1px 7px" },
  contact: { fontSize: 12, color: "#5D5D5D" },
  inactive: { opacity: 0.5 },
  toggleBtn: { fontSize: 11.5, fontWeight: 600, padding: "4px 9px", borderRadius: 6, border: "1px solid #D9D9D9",
               background: "#fff", cursor: "pointer" },
  addGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 14 },
  input: { padding: "8px 10px", fontSize: 12.5, border: "1px solid #D9D9D9", borderRadius: 6, fontFamily: "inherit" },
  addBtn: { gridColumn: "1 / -1", padding: "8px 14px", fontSize: 12.5, fontWeight: 600, border: "none", borderRadius: 6,
            background: "#262626", color: "#fff", cursor: "pointer" },
  empty: { fontSize: 12.5, color: "#8C8C8C", padding: "16px 0" },
  err: { color: "#A8071A", fontSize: 12.5, marginBottom: 10 },
};

export default function CqcContributors({ sites, onClose, onChanged }) {
  const [siteId, setSiteId] = useState((sites && sites[0] && sites[0]._id) || "");
  const [contributors, setContributors] = useState([]);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load(sid) {
    if (!sid) { setContributors([]); return; }
    try {
      const data = await cqcApi.listContributors(sid);
      setContributors(data.contributors || []);
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => { load(siteId); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [siteId]);

  async function addContributor(e) {
    e && e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSaving(true); setError(null);
    try {
      await cqcApi.saveContributor({ siteId, name: name.trim(), role: role.trim() || null, email: email.trim(), mobile: mobile.trim() || null });
      setName(""); setRole(""); setEmail(""); setMobile("");
      await load(siteId);
      onChanged && onChanged();
    } catch (e2) {
      setError(e2.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(c) {
    setSaving(true); setError(null);
    try {
      await cqcApi.saveContributor({ contributorId: c._id, active: !c.active });
      await load(siteId);
      onChanged && onChanged();
    } catch (e2) {
      setError(e2.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.head}>
          <h2 style={S.title}>Contributors</h2>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={S.body}>
          <select style={S.siteSelect} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            {(sites || []).map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>

          {error && <div style={S.err}>{error}</div>}

          {contributors.length === 0
            ? <div style={S.empty}>No contributors added for this site yet.</div>
            : contributors.map((c) => (
                <div key={c._id} style={{ ...S.row, ...(c.active === false ? S.inactive : {}) }}>
                  <div style={{ flex: 1 }}>
                    <div style={S.name}>
                      {c.name}{c.role ? <> {" "}<span style={S.roleTag}>{c.role}</span></> : ""}
                    </div>
                    <div style={S.contact}>{c.email}{c.mobile ? ` · ${c.mobile}` : ""}</div>
                  </div>
                  <button style={S.toggleBtn} disabled={saving} onClick={() => toggleActive(c)}>
                    {c.active === false ? "Reactivate" : "Deactivate"}
                  </button>
                </div>
              ))}

          <form style={S.addGrid} onSubmit={addContributor}>
            <input style={S.input} placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
            <input style={S.input} placeholder="Role (optional)" value={role} onChange={(e) => setRole(e.target.value)} />
            <input style={S.input} placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <input style={S.input} placeholder="Mobile (optional)" type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} />
            <button style={S.addBtn} type="submit" disabled={saving || !name.trim() || !email.trim()}>Add</button>
          </form>
        </div>
      </div>
    </div>
  );
}
